'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavItem {
  label: string;
  href: string;
  isPage?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Services', href: '/services', isPage: true },
  { label: 'About', href: '#about' },
  { label: 'Process', href: '#process' },
  { label: 'Estimate', href: '#estimate' },
  { label: 'Client Portal', href: '#portal' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, isPage?: boolean) => {
    setMenuOpen(false);
    if (isPage) return;
    if (!isHome && href.startsWith('#')) {
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        scrolled ? 'shadow-nav' : 'border-b border-brand-grey100'
      }`}
    >
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20">
          {/* Logo */}
          <Link
            href="/"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 group"
          >
            <div className="flex items-center">
              <span
                className="font-serif text-xl sm:text-2xl font-bold text-brand-dark tracking-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-1px' }}
              >
                SYHJ
              </span>
              <span
                className="ml-2 w-2 h-2 rounded-full bg-primary inline-block"
                aria-hidden="true"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main Navigation">
            {navItems.map((item) => (
              item.isPage ? (
                <Link
                  key={item.label}
                  href={item.href}
                  className={`nav-link text-brand-dark font-sans text-base font-normal py-4 px-0 relative transition-colors duration-300 hover:text-primary ${pathname === item.href ? 'text-primary' : ''}`}
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.href, item.isPage)}
                  className="nav-link text-brand-dark font-sans text-base font-normal py-4 px-0 relative transition-colors duration-300 hover:text-primary bg-transparent border-none cursor-pointer"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </button>
              )
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              href="/client-portal"
              className={`text-brand-dark/50 hover:text-brand-dark font-sans text-sm transition-colors duration-200 ${pathname === '/client-portal' ? 'text-primary font-semibold' : ''}`}
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
            >
              Client Login
            </Link>
            <button
              onClick={() => handleNavClick('#portal')}
              className="btn-primary text-sm px-6 py-3"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-3 -mr-1 text-brand-dark touch-manipulation"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <div className="w-6 flex flex-col gap-1.5">
              <span className={`block h-0.5 bg-brand-dark transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-0.5 bg-brand-dark transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 bg-brand-dark transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-brand-grey100 py-2 pb-4">
            {navItems.map((item) => (
              item.isPage ? (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className={`block w-full text-left py-4 px-3 font-sans text-base border-b border-brand-grey100 last:border-0 transition-colors duration-200 touch-manipulation ${pathname === item.href ? 'text-primary' : 'text-brand-dark hover:text-primary'}`}
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', display: 'flex', alignItems: 'center' }}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.href, item.isPage)}
                  className="block w-full text-left py-4 px-3 text-brand-dark font-sans text-base border-b border-brand-grey100 last:border-0 hover:text-primary transition-colors duration-200 bg-transparent cursor-pointer touch-manipulation"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px' }}
                >
                  {item.label}
                </button>
              )
            ))}
            <button
              onClick={() => handleNavClick('#portal')}
              className="btn-primary w-full text-center mt-4 text-sm py-4 touch-manipulation"
              style={{ minHeight: '48px' }}
            >
              Get Started
            </button>
            <Link
              href="/client-portal"
              onClick={() => setMenuOpen(false)}
              className={`block w-full text-center py-3 px-3 font-sans text-sm mt-2 hover:text-brand-dark transition-colors duration-200 touch-manipulation ${pathname === '/client-portal' ? 'text-primary font-semibold' : 'text-brand-dark/40'}`}
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
            >
              Client Login →
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
